// react
import { ChangeEvent } from 'react';

// types
import {
	PermissionCrud,
	PermissionOperation,
	PermissionResource,
	PermissionsByResource,
} from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// ui
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';

// links
import { Checkbox, SxProps, Typography } from '@mui/material';

// utils
import { permissionResourceMap } from '../../../../utils/permissions';
import { toCamelCase } from '../../../../utils/strings';

// props
export interface TableBasicPermissionsProps extends Omit<TableProps, 'data' | 'headers'> {
	data: PermissionsByResource[];
	onChange: (
		e: ChangeEvent<HTMLInputElement>,
		resource: PermissionResource,
		operation: PermissionOperation
	) => void;
	values: Record<string, Omit<PermissionCrud, 'delete'>>;
}

export const TableBasicPermissions = ({
	data = [],
	onChange,
	values,
	...props
}: TableBasicPermissionsProps) => {
	// hooks
	const { t } = useTranslation();

	const sxCheckbox: SxProps = {
		padding: 0,
	};

	// vars
	const headers: TableProps['headers'] = [
		{
			label: null,
			render: (label: string) => {
				return <Typography fontWeight={600}>{label}</Typography>;
			},
			value: 'label',
		},
		{
			align: 'center',
			label: t('approve'),
			render: (resource: PermissionResource, header: TableHeader, item: PermissionsByResource) => {
				if (['CONTRACT', 'INVOICE', 'PAYMENT'].includes(resource)) {
					return (
						<Checkbox
							checked={values[permissionResourceMap[resource]]?.approve}
							color="primary"
							disableRipple={true}
							name={`${toCamelCase(resource)}Approve`}
							onChange={(e) => onChange(e, resource, 'APPROVE')}
							sx={sxCheckbox}
						/>
					);
				}

				return '';
			},
			value: 'resource',
		},
		{
			align: 'center',
			label: t('pii'),
			render: (resource: PermissionResource, header: TableHeader, item: PermissionsByResource) => {
				if (['BENEFICIARY'].includes(resource)) {
					return (
						<Checkbox
							checked={values[permissionResourceMap[resource]]?.readPii}
							color="primary"
							disableRipple={true}
							name={`${toCamelCase(resource)}ReadPii`}
							onChange={(e) => onChange(e, resource, 'READ_PII')}
							sx={sxCheckbox}
						/>
					);
				}

				return '';
			},
			value: 'resource',
		},
		{
			align: 'center',
			label: t('create'),
			render: (resource: PermissionResource, header: TableHeader, item: PermissionsByResource) => {
				return (
					<Checkbox
						checked={values[permissionResourceMap[resource]]?.create}
						color="primary"
						disableRipple={true}
						name={`${toCamelCase(resource)}Create`}
						onChange={(e) => onChange(e, resource, 'CREATE')}
						sx={sxCheckbox}
					/>
				);
			},
			value: 'resource',
		},
		{
			align: 'center',
			label: t('update'),
			render: (resource: PermissionResource, header: TableHeader, item: PermissionsByResource) => {
				return (
					<Checkbox
						checked={values[permissionResourceMap[resource]]?.update}
						color="primary"
						disableRipple={true}
						name={`${toCamelCase(resource)}Update`}
						onChange={(e) => onChange(e, resource, 'UPDATE')}
						sx={sxCheckbox}
					/>
				);
			},
			value: 'resource',
		},
		{
			align: 'center',
			label: t('read'),
			render: (resource: PermissionResource, header: TableHeader, item: PermissionsByResource) => {
				return (
					<Checkbox
						checked={values[permissionResourceMap[resource]]?.read}
						color="primary"
						disableRipple={true}
						name={`${toCamelCase(resource)}Read`}
						onChange={(e) => onChange(e, resource, 'READ')}
						sx={sxCheckbox}
					/>
				);
			},
			value: 'resource',
		},
	];

	return (
		<Table
			borderRadius={2}
			color="grey"
			hasBorder={true}
			hasDividers={true}
			{...props}
			data={data}
			headers={headers}
		/>
	);
};
